/**
 * Grade Formatting Utilities
 * Extracted from ResultAndRevision.js for better organization
 */

import { isEnglishCategory } from './categoryHelpers.js';

/**
 * Format grade text with proper styling and make headers bold
 */
export function formatGradeText(text) {
  if (!text) return '';
  
  // Check if this is a teacher-edited grade
  const isTeacherEdited = text.includes("[TEACHER]");
  
  // Remove the [TEACHER] marker for display but keep track of it
  let displayText = text;
  if (isTeacherEdited) {
    displayText = text.replace(/\s*\[TEACHER\]\s*/, '');
  }
  
  // Replace the main headers with bold/styled versions
  let formattedText = displayText
    // Main "Total Score Calculation" header - make it the most prominent
    .replace(/Total\s+Score\s+Calculation:?/g, '<span class="grade-header-main">Total Score Calculation</span>')
    // Other section headers
    .replace(/Purpose\s*:?/g, '<span class="grade-header">Purpose:</span>')
    .replace(/Coherence\s*:?/g, '<span class="grade-header">Coherence:</span>')
    .replace(/Language\s+Use\s*:?/g, '<span class="grade-header">Language Use:</span>')
    .replace(/Accuracy\s+of\s+Mechanics\s*:?/g, '<span class="grade-header">Accuracy of Mechanics:</span>')
    .replace(/Final\s+Mark\s*:?/g, '<span class="grade-header-final">Final Mark:</span>')
    .replace(/Overall\s+Score\s*:?/g, '<span class="grade-header-final">Overall Score:</span>')
    .replace(/Final\s+Grade\s*:?/g, '<span class="grade-header-final">Final Grade:</span>')
    // Replace newlines with <br> tags
    .replace(/\n/g, '<br />');
  
  // If teacher-edited, wrap the entire content in a special container
  if (isTeacherEdited) {
    formattedText = `
      <div class="teacher-edited-grade">
        <span class="teacher-grade-badge">Teacher Edited</span>
        ${formattedText}
      </div>
    `;
  }
  
  return formattedText;
}

/**
 * Get word count warning for essays that are below target word count
 */
export function getWordCountWarning(category, wordCount) {
  if (!isEnglishCategory(category) || !wordCount) return null;
  
  let targetWordCount = 0;
  
  // Set target word count based on category
  if (category === 'Poetry') {
    targetWordCount = 700;
  } else if (category === 'Shakespeare') {
    targetWordCount = 800;
  } else if (category === 'Comparative') {
    targetWordCount = 900;
  } else if (category === 'Composition') {
    targetWordCount = 1000;
  } else {
    return null; // No warning for other categories
  }
  
  // Only show warning if word count is below target
  if (wordCount < targetWordCount) {
    return (
      <div className="word-count-warning" style={{
        backgroundColor: '#fff3cd',
        color: '#856404',
        padding: '10px 15px',
        marginTop: '15px',
        marginBottom: '15px',
        borderRadius: '4px',
        borderLeft: '4px solid #ffc107'
      }}>
        <p style={{ margin: 0 }}>
          Note: your essay is {wordCount} words long. A {category} essay may be marked down if it is below {targetWordCount} words. 
          This is reflected in Pulc's grade below, but some examiners may penalise more or less than Pulc does.
        </p>
      </div>
    );
  }
  
  return null;
}

/**
 * Convert JSON grades to editable text format
 */
export function jsonGradesToText(grades, customGradeText = null) {
  // Check if there's custom grade text to use instead
  if (customGradeText) {
    return customGradeText;
  }
  
  let text = "Total Score Calculation\n\n";
  
  // Handle VCE Section A/C categories
  if (grades.knowledge) {
    text += `Knowledge: ${grades.knowledge.score}/${grades.knowledge.max}`;
    if (grades.knowledge.feedback) {
      text += `\n${grades.knowledge.feedback}`;
    }
    text += "\n\n";
  }
  if (grades.coherence && grades.knowledge) { // VCE format (has knowledge)
    text += `Coherence: ${grades.coherence.score}/${grades.coherence.max}`;
    if (grades.coherence.feedback) {
      text += `\n${grades.coherence.feedback}`;
    }
    text += "\n\n";
  }
  if (grades.evidence) {
    text += `Evidence: ${grades.evidence.score}/${grades.evidence.max}`;
    if (grades.evidence.feedback) {
      text += `\n${grades.evidence.feedback}`;
    }
    text += "\n\n";
  }
  
  // Handle Australian HSC categories
  if (grades.engagement) {
    text += `Engagement: ${grades.engagement.score}/${grades.engagement.max}`;
    if (grades.engagement.feedback) {
      text += `\n${grades.engagement.feedback}`;
    }
    text += "\n\n";
  }
  if (grades.purpose) {
    text += `Purpose: ${grades.purpose.score}/${grades.purpose.max}`;
    if (grades.purpose.feedback) {
      text += `\n${grades.purpose.feedback}`;
    }
    text += "\n\n";
  }
  if (grades.structure) {
    text += `Structure: ${grades.structure.score}/${grades.structure.max}`;
    if (grades.structure.feedback) {
      text += `\n${grades.structure.feedback}`;
    }
    text += "\n\n";
  }
  if (grades.analysis) {
    text += `Analysis: ${grades.analysis.score}/${grades.analysis.max}`;
    if (grades.analysis.feedback) {
      text += `\n${grades.analysis.feedback}`;
    }
    text += "\n\n";
  }
  
  // Language: prefer language, fallback to language_use
  const languageBlock = grades.language || grades.language_use;
  if (languageBlock) {
    const langLabel = grades.language ? 'Language' : 'Language Use';
    text += `${langLabel}: ${languageBlock.score}/${languageBlock.max}`;
    if (languageBlock.feedback) {
      text += `\n${languageBlock.feedback}`;
    }
    text += "\n\n";
  }
  
  // Handle standard English categories (only if not VCE/HSC format)
  if (!grades.knowledge && !grades.engagement && grades.coherence) {
    text += `Coherence: ${grades.coherence.score}/${grades.coherence.max}`;
    if (grades.coherence.feedback) {
      text += `\n${grades.coherence.feedback}`;
    }
    text += "\n\n";
  }
  
  // Mechanics: prefer mechanics, fallback to accuracy_of_mechanics
  const mechanicsBlock = grades.mechanics || grades.accuracy_of_mechanics;
  if (mechanicsBlock) {
    const mechScore = mechanicsBlock.score === "N/A" ? "N/A" : `${mechanicsBlock.score}/${mechanicsBlock.max}`;
    text += `Accuracy of Mechanics: ${mechScore}`;
    if (mechanicsBlock.feedback) {
      text += `\n${mechanicsBlock.feedback}`;
    }
    text += "\n\n";
  }
  
  if (grades.total) {
    text += `Final Mark: ${grades.total.score}/${grades.total.max} (${grades.total.percentage}%)`;
  }
  return text;
} 